import { useState } from "react";
import { Button } from "../../../components/tailwindComponent/ui/button";
import { Card, CardContent } from "../../../components/tailwindComponent/ui/card";
import { getCategoryStyles } from "../../../utils/utils";

interface BestPracticeCardProps {
  practice: any;
  categoryColor: string;
  categoryIcon: any;
  actionDetails?: any[];
}

export function BestPracticeCard({
  practice,
  categoryColor,
  categoryIcon: CategoryIcon,
  actionDetails = []
}: BestPracticeCardProps) {
  const styles = getCategoryStyles(categoryColor);
  const [showDetails, setShowDetails] = useState(false);

  const handleQuickSimulation = () => {
    // TODO: implement quick simulation
  };

  const handleDetailedSimulation = () => {
    setShowDetails(prev => !prev);
  };

  return (
    <Card className="border border-muted transition-all duration-300">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-md ${styles.background} flex-shrink-0`}>
            <CategoryIcon className={`h-5 w-5 ${styles.icon}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg leading-tight">{practice.process}</h3>
            {practice.notes && (
              <p className="text-md text-muted-foreground mt-1">{practice.notes}</p>
            )}
          </div>
        </div>

        <div className="flex gap-2 mt-2">
          <Button variant="simulate" size="sm" className="flex-1" onClick={handleQuickSimulation}>
            Simulate
          </Button>
          <Button
            variant={showDetails ? "default" : "outline"}
            size="sm"
            onClick={handleDetailedSimulation}
          >
            {showDetails ? "Hide Details" : "Detailed"}
          </Button>
        </div>

        {showDetails && (
          <div className="mt-2 p-3 rounded-md bg-muted/30 text-sm space-y-3 animate-in fade-in duration-200">
            {/* --- Process Info --- */}
            <div className="space-y-1">
              <h4 className="text-sm font-semibold text-foreground">Process Info</h4>
              <div className="grid grid-cols-2 gap-2">
                {practice.type && (
                  <div className="flex">
                    <span className="flex-1 text-muted-foreground font-bold">Type:</span>
                    <span className="flex-1 font-medium">{practice.type}</span>
                  </div>
                )}
                {practice.quantity_kg && (
                  <div className="flex ">
                    <span className="flex-1 text-muted-foreground font-bold">Quantity (kg):</span>
                    <span className="flex-1 font-medium">{practice.quantity_kg}</span>
                  </div>
                )}
                {practice.quantity_percent && (
                  <div className="flex">
                    <span className="flex-1 text-muted-foreground font-bold">Quantity (%):</span>
                    <span className="flex-1 font-medium">{practice.quantity_percent}</span>
                  </div>
                )}
                {practice.category && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground font-bold">Category:</span>
                    <span className="font-medium">{practice.category}</span>
                  </div>
                )}
              </div>
            </div>

            {/* --- Additional Action Details (from your array) --- */}
            {actionDetails.map((detail, idx) => {
              const linkAvailable = detail.link != "N/A";
              return (
                <div key={idx} className="border-t border-muted pt-2 space-y-1 animate-in fade-in">
                  {detail.description && (
                    <p>
                      <span className="text-muted-foreground font-bold">Description:</span>{" "}
                      {detail.description}
                    </p>
                  )}
                  <div className="space-y-1">
                    {detail.expected_reduction_percent && (
                      <p>
                        <span className="text-muted-foreground font-bold">Expected Reduction:</span>{" "}
                        {detail.expected_reduction_percent}
                      </p>
                    )}
                    {detail.expected_revenue_usd && (
                      <p>
                        <span className="text-muted-foreground font-bold">Expected Revenue:</span>{" "}
                        {detail.expected_revenue_usd}
                      </p>
                    )}
                    {detail.trade_offs && (
                      <p>
                        <span className="text-muted-foreground font-bold">Trade-offs:</span>{" "}
                        {detail.trade_offs}
                      </p>
                    )}
                    {detail.source && (
                      <p className="text-muted-foreground text-xs italic mt-1 text-blue-500">
                        {linkAvailable ? (
                          <a
                            href={detail.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-600 underline"
                          >
                            Source: {detail.source}
                          </a>
                        ) : (
                          <>Source: {detail.source}</>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
